import React, { useEffect, useRef, useState } from 'react';
import Table, { Column, TableHandle } from '../../components/Table';
import { Button } from '../../components/Button';
import { Dropdown } from '../../components/Dropdown';
import Modal from '../../components/Modal';
import { apiGet, apiPut } from '../../services/apiServices';
import moment from 'moment';
import { toast } from 'react-toastify';
import { endPoints } from '../../configs/apiUrls';

// Import your new filter form component
import RequestSearchFilterForm from './RequestSearchFilterForm';

const Requests = () => {
  const tableRef = useRef<TableHandle>(null);

  const [requestsList, setRequestsList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false)

  const [filterData, setFilterData] = useState({
    page: 1,
    limit: 10,
    search: "",
    status: "",
    state: "",
    city: "",
    area: "",
    valuer: "",
    fromDate: "",
    toDate: ""
  });

  // Assignment Modal States
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [availableValuers, setAvailableValuers] = useState<any[]>([]);
  const [selectedValuer, setSelectedValuer] = useState("");
  const [matchLevel, setMatchLevel] = useState("");
  const [assigning, setAssigning] = useState(false);

  // View Details Modal States
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState<any>(null);

  // --- Fetch Requests ---
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: filterData.page.toString(),
        limit: filterData.limit.toString(),
        ...(filterData.status && { status: filterData.status }),
        ...(filterData.search && { search: filterData.search }),
        ...(filterData.state && { state: filterData.state }),
        ...(filterData.city && { city: filterData.city }),
        ...(filterData.area && { area: filterData.area }),
        ...(filterData.valuer && { valuer: filterData.valuer }),
        ...(filterData.fromDate && { fromDate: filterData.fromDate }),
        ...(filterData.toDate && { toDate: filterData.toDate }),
      });

      const res: any = await apiGet(`${endPoints.GET_ALL_REQUESTS}?${params.toString()}`);
      if (res.success) {
        setRequestsList(res.data);
        setTotalCount(res.total);
      }
    } catch (error) {
      toast.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [
    filterData.page, filterData.limit, filterData.status, filterData.search,
    filterData.state, filterData.city, filterData.area, filterData.valuer,
    filterData.fromDate, filterData.toDate, refresh
  ]);

  // --- Open Assign Modal & Fetch Valuers ---
  const handleOpenAssignModal = async (row: any) => {
    setSelectedRequest(row);
    setIsAssignModalOpen(true);
    setAvailableValuers([]);
    setSelectedValuer("");

    try {
      const { state, city, area } = row.requestInfo;
      const query = `?state=${state || ""}&city=${city || ""}&area=${area || ""}`;
      const res: any = await apiGet(`${endPoints.GET_LOCATION_VALUERS}${query}`);

      if (res.success) {
        const options = res.data.map((v: any) => ({
          label: `${v.name} (${v.mobileNumber})`,
          value: v._id
        }));
        setAvailableValuers(options);
        setMatchLevel(res.matchLevel);
      }
    } catch (error) {
      toast.error("Failed to load valuers");
    }
  };

  // --- Submit Assignment ---
  const submitAssignment = async () => {
    if (!selectedValuer) return toast.warning("Please select a valuer first");

    setAssigning(true);
    try {
      const res: any = await apiPut(endPoints.ASSIGN_VALUER(selectedRequest._id), { valuerId: selectedValuer });
      if (res.success) {
        toast.success("Valuer assigned successfully");
        setIsAssignModalOpen(false);
        fetchRequests();
      }
    } catch (error) {
      toast.error("Failed to assign valuer");
    } finally {
      setAssigning(false);
    }
  };

  // --- Open View Details Modal ---
  const handleOpenViewModal = (row: any) => {
    setViewData(row);
    setIsViewModalOpen(true);
  };

  // --- Table Columns ---
  const columns: Column<any>[] = [
    {
      key: "createdAt",
      label: "Date",
      customCell: (row) => (
        <span className='text-sm'>{moment(row.createdAt).format("DD MMM YYYY")}</span>
      )
    },
    {
      key: "createdBy",
      label: "Customer",
      customCell: (row) => (
        <div className='flex flex-col text-sm'>
          <span className="font-semibold">{`${row?.createdBy?.name || "-"} (${row?.createdBy?.userType})`}</span>
          <span className="text-gray-500">{row?.createdBy?.mobileNumber}</span>
        </div>
      )
    },
    {
      key: "type",
      label: "Request Type",
      customCell: (row) => (
        <div className='flex flex-col text-sm'>
          <span className="font-medium">{row.type}</span>
          <span className="text-gray-500 text-xs">
            {row.requestInfo?.purpose || row.requestInfo?.serviceType || row.requestInfo?.proposal}
          </span>
        </div>
      )
    },
    {
      key: "location",
      label: "Location",
      customCell: (row) => (
        <div className='flex flex-col text-sm text-gray-600'>
          <span>{row.requestInfo?.area}, {row.requestInfo?.city}</span>
          <span className="text-xs">{row.requestInfo?.state}</span>
        </div>
      )
    },
    {
      key: "status",
      label: "Status",
      customCell: (row) => {
        let badgeColor = "bg-gray-100 text-gray-700";
        if (row.status === "Requested") badgeColor = "bg-yellow-100 text-yellow-700";
        if (row.status === "Assigned") badgeColor = "bg-blue-100 text-blue-700";
        if (row.status === "Request Accepted") badgeColor = "bg-purple-100 text-purple-700";
        if (row.status === "Completed") badgeColor = "bg-green-100 text-green-700";
        if (row.status === "Request Rejected") badgeColor = "bg-red-100 text-red-700 border border-red-300";
        if (row.status === "Cancelled") badgeColor = "bg-red-100 text-red-700";

        return (
          <span className={`px-2 py-1 rounded text-xs font-semibold ${badgeColor}`}>
            {row.status}
          </span>
        );
      }
    },
    {
      key: "assignedTo",
      label: "Assigned Valuer",
      customCell: (row) => (
        <div className='text-sm'>
          {row.assignedTo ? (
            <div className="flex flex-col">
              <span className="font-medium text-blue-600">{row.assignedTo.name}</span>
              <span className="text-xs text-gray-500">{row.assignedTo.mobileNumber}</span>
            </div>
          ) : (
            <span className="text-gray-400 italic">Not Assigned</span>
          )}
        </div>
      )
    },
    {
      key: "action",
      label: "Action",
      sortable: false,
      customCell: (row) => (
        <div>
          {row.status === "Requested" || row.status === "Request Rejected" ? (
            <Button
              variant={row.status === "Request Rejected" ? "error" : "primary"}
              onClick={() => handleOpenAssignModal(row)}
            >
              {row.status === "Request Rejected" ? "Reassign" : "Assign"}
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => handleOpenViewModal(row)}>
              View
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Requests Management</h2>

      <Table
        ref={tableRef}
        columns={columns}
        data={requestsList?.length ? requestsList : []}
        tableName="Requests"
        totalCount={totalCount}
        currentPage={filterData.page}
        pageSize={filterData.limit}
        loading={loading}
        onPageChange={(page) => setFilterData({ ...filterData, page })}
        onPageSizeChange={(size) => setFilterData({ ...filterData, limit: size, page: 1 })}
        onSearchChange={(searchText) => {
          setFilterData({ ...filterData, search: searchText, page: 1 });
        }}
        filterContent={
          <RequestSearchFilterForm
            tableRef={tableRef}
            filterData={filterData}
            setFilterData={setFilterData}
          />
        }
        renderRightToolItems={
          <div className='flex gap-2'>
            <Button
              variant="primary"
              onClick={() => setRefresh(!refresh)}
            >
              Refresh
            </Button>
          </div>

        }
      />

      {/* --- ASSIGN VALUER MODAL --- */}
      <Modal isOpen={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} title={selectedRequest?.status === "Request Rejected" ? "Reassign Valuer" : "Assign Valuer"}>
        <div className="p-4">

          {selectedRequest?.status === "Request Rejected" && (
            <div className="mb-4 text-sm text-red-700 bg-red-50 p-3 rounded border border-red-200">
              ⚠️ The previously assigned professional rejected this request. Please assign a new one.
            </div>
          )}

          <div className="mb-4 text-sm text-gray-600 bg-gray-50 p-3 rounded">
            <p><strong>Property Location:</strong> {selectedRequest?.requestInfo?.area}, {selectedRequest?.requestInfo?.city}, {selectedRequest?.requestInfo?.state}</p>
          </div>

          <label className="block text-sm font-medium text-gray-700 mb-1">
            Available Professionals
            {matchLevel && <span className="text-blue-600 text-xs ml-2">(Matched by {matchLevel})</span>}
          </label>

          {availableValuers.length > 0 ? (
            <Dropdown
              options={availableValuers}
              value={selectedValuer}
              onChange={(val: string) => setSelectedValuer(val)}
              placeholder="Select a Professional"
            />
          ) : (
            <div className="text-red-500 text-sm mt-2">
              No professionals found in this State/City/Area. Please register one in this location first.
            </div>
          )}

          <div className="flex justify-end gap-3 mt-32 mb-8">
            <Button variant="secondary" onClick={() => setIsAssignModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitAssignment}
              disabled={assigning || availableValuers.length === 0}
            >
              {assigning ? "Assigning..." : selectedRequest?.status === "Request Rejected" ? "Confirm Reassignment" : "Confirm Assignment"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* --- VIEW DETAILS MODAL --- */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Request Details">
        {viewData && (
          <div className="p-4 space-y-5 text-sm">
            {/* Customer Details */}
            <div>
              <h3 className="font-semibold text-gray-800 border-b pb-1 mb-2">Customer Details</h3>
              <p><span className="font-medium text-gray-600">Name:</span> {viewData.requestInfo?.customerName || viewData.createdBy?.name}</p>
              <p><span className="font-medium text-gray-600">Contact:</span> {viewData.requestInfo?.contactNo || viewData.createdBy?.mobileNumber}</p>
            </div>

            {/* Request Details */}
            <div>
              <h3 className="font-semibold text-gray-800 border-b pb-1 mb-2">Request Information</h3>
              <p><span className="font-medium text-gray-600">Type:</span> {viewData.type}</p>
              {viewData.requestInfo?.propertyType && <p><span className="font-medium text-gray-600">Property Type:</span> {viewData.requestInfo.propertyType}</p>}
              {viewData.requestInfo?.purpose && <p><span className="font-medium text-gray-600">Purpose:</span> {viewData.requestInfo.purpose}</p>}
              {viewData.requestInfo?.serviceType && <p><span className="font-medium text-gray-600">Service:</span> {viewData.requestInfo.serviceType}</p>}
              {viewData.requestInfo?.proposal && <p><span className="font-medium text-gray-600">Proposal:</span> {viewData.requestInfo.proposal}</p>}
              <p><span className="font-medium text-gray-600">Location:</span> {viewData.requestInfo?.area}, {viewData.requestInfo?.city}, {viewData.requestInfo?.state}</p>
              {viewData.requestInfo?.propertyArea && <p><span className="font-medium text-gray-600">Property Area:</span> {viewData.requestInfo.propertyArea}</p>}
              {viewData.requestInfo?.remarks && <p><span className="font-medium text-gray-600">Remarks:</span> {viewData.requestInfo.remarks}</p>}
            </div>

            {/* Assigned Professional */}
            {viewData.assignedTo && (
              <div>
                <h3 className="font-semibold text-gray-800 border-b pb-1 mb-2">Assigned Professional</h3>
                <p><span className="font-medium text-gray-600">Name:</span> {viewData.assignedTo.name}</p>
                <p><span className="font-medium text-gray-600">Contact:</span> {viewData.assignedTo.mobileNumber}</p>
              </div>
            )}

            {/* Cancellation Reason */}
            {viewData.status === "Cancelled" && (
              <div className="bg-red-50 p-3 rounded border border-red-200">
                <h3 className="font-semibold text-red-800 mb-1">Cancellation Reason</h3>
                <p className="text-red-700">{viewData.cancellationReason || "No reason provided."}</p>
              </div>
            )}

            {/* Rating & Review */}
            {viewData.status === "Completed" && viewData.rating && (
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <h3 className="font-semibold text-green-800 mb-1">Customer Rating & Feedback</h3>
                <p className="text-green-700 flex items-center">
                  <span className="font-medium mr-2">Rating:</span>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <span key={idx} className={idx < viewData.rating ? "text-yellow-500 text-lg" : "text-gray-300 text-lg"}>★</span>
                  ))}
                </p>
                {viewData.review && (
                  <p className="text-green-700 mt-1"><span className="font-medium">Review:</span> <span className="italic">"{viewData.review}"</span></p>
                )}
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button variant="secondary" onClick={() => setIsViewModalOpen(false)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default Requests;